/**
 * Centralized mappings for the Chariot UI
 * This file serves as the single source of truth for all data mappings
 * to ensure consistency across the application
 */

// ============================================
// VULNERABILITY MAPPINGS
// ============================================

/**
 * Vulnerability field definitions
 * Used in QueryBuilder and other components for field configuration
 */
export const VULNERABILITY_FIELDS = [
  {
    name: 'priority',
    label: 'Severity',
    type: 'select' as const,
    options: [
      { label: 'Critical', value: '0' },
      { label: 'High', value: '10' },
      { label: 'Medium', value: '20' },
      { label: 'Low', value: '30' },
      { label: 'Info', value: '40' },
      { label: 'Exposure', value: '50' },
    ],
  },
  {
    name: 'status',
    label: 'Status',
    type: 'select' as const,
    options: [
      { label: 'Open', value: 'O' },
      { label: 'Pending', value: 'P' },
      { label: 'Accepted', value: 'I' },
      { label: 'Closed', value: 'R' },
    ],
  },
  {
    name: 'cvss',
    label: 'CVSS Score',
    type: 'number' as const,
  },
  {
    name: 'epss',
    label: 'EPSS Score',
    type: 'number' as const,
  },
  {
    name: 'kev',
    label: 'KEV',
    type: 'boolean' as const,
  },
  {
    name: 'tags',
    label: 'Tags',
    type: 'string' as const,
  },
  {
    name: 'created',
    label: 'Created',
    type: 'date' as const,
  },
  {
    name: 'updated',
    label: 'Updated',
    type: 'date' as const,
  },
];

/**
 * Vulnerability severity to priority mapping
 * Used for converting UI severity labels to backend priority numbers
 */
export const SEVERITY_TO_PRIORITY_MAP: Record<string, number> = {
  Critical: 0,
  High: 10,
  Medium: 20,
  Low: 30,
  Info: 40,
  Exposure: 50,
};

/**
 * Severity code to priority mapping
 * Maps single character severity codes to priority numbers
 */
export const SEVERITY_CODE_TO_PRIORITY_MAP: Record<string, number> = {
  C: 0, // Critical
  H: 10, // High
  M: 20, // Medium
  L: 30, // Low
  I: 40, // Info
  E: 50, // Exposure
};

/**
 * Priority to severity mapping (inverse of above)
 * Used for converting backend priority numbers to UI severity labels
 */
export const PRIORITY_TO_SEVERITY_MAP: Record<number, string> = Object.entries(
  SEVERITY_TO_PRIORITY_MAP
).reduce(
  (acc, [severity, priority]) => {
    acc[priority] = severity;
    return acc;
  },
  {} as Record<number, string>
);

/**
 * Vulnerability status value mapping
 * Maps UI status labels to backend status codes
 */
export const VULNERABILITY_STATUS_MAP: Record<string, string> = {
  Pending: 'T', // "T" for Triage
  Open: 'O',
  Accepted: 'I', // "I" for Ignored/Accepted
  Closed: 'R', // "R" for Remediated
};

/**
 * Inverse vulnerability status mapping
 * Maps backend status codes to UI labels
 */
export const VULNERABILITY_STATUS_CODE_TO_LABEL: Record<string, string> = {
  P: 'Pending',
  O: 'Open',
  I: 'Accepted',
  R: 'Closed',
  T: 'Triage',
};

/**
 * Full vulnerability status definitions for detailed status strings
 * Maps composite status codes (e.g., "TH", "OC") to their components
 */
export const VULNERABILITY_STATUS_DEFINITIONS: Record<
  string,
  { state: string; severity: string }
> = {
  // Triage statuses
  TI: { state: 'Triage', severity: 'Info' },
  TL: { state: 'Triage', severity: 'Low' },
  TM: { state: 'Triage', severity: 'Medium' },
  TH: { state: 'Triage', severity: 'High' },
  TC: { state: 'Triage', severity: 'Critical' },
  TE: { state: 'Triage', severity: 'Exposure' },

  // Open statuses
  OI: { state: 'Open', severity: 'Info' },
  OL: { state: 'Open', severity: 'Low' },
  OM: { state: 'Open', severity: 'Medium' },
  OH: { state: 'Open', severity: 'High' },
  OC: { state: 'Open', severity: 'Critical' },
  OE: { state: 'Open', severity: 'Exposure' },

  // Accepted (Ignored) statuses
  II: { state: 'Accepted', severity: 'Info' },
  IL: { state: 'Accepted', severity: 'Low' },
  IM: { state: 'Accepted', severity: 'Medium' },
  IH: { state: 'Accepted', severity: 'High' },
  IC: { state: 'Accepted', severity: 'Critical' },
  IE: { state: 'Accepted', severity: 'Exposure' },

  // Remediated statuses
  RI: { state: 'Remediated', severity: 'Info' },
  RL: { state: 'Remediated', severity: 'Low' },
  RM: { state: 'Remediated', severity: 'Medium' },
  RH: { state: 'Remediated', severity: 'High' },
  RC: { state: 'Remediated', severity: 'Critical' },
  RE: { state: 'Remediated', severity: 'Exposure' },
};

// ============================================
// ASSET MAPPINGS
// ============================================

/**
 * Asset field definitions
 * Used in QueryBuilder and other components for field configuration
 */
export const ASSET_FIELDS = [
  {
    name: 'dns',
    label: 'FQDN',
    type: 'string' as const,
  },
  {
    name: 'name',
    label: 'IP Address',
    type: 'string' as const,
  },
  {
    name: 'identifier',
    label: 'Identifier',
    type: 'string' as const,
  },
  {
    name: 'group',
    label: 'Group',
    type: 'string' as const,
  },
  {
    name: 'status',
    label: 'Status',
    type: 'select' as const,
    options: [
      { label: 'Approved', value: 'A' },
      { label: 'Pending', value: 'P' },
      { label: 'Unavailable', value: 'U' },
      { label: 'Frozen', value: 'F' },
      { label: 'Deleted', value: 'D' },
      { label: 'Rejected', value: 'FR' },
    ],
  },
  {
    name: 'country',
    label: 'Country',
    type: 'string' as const,
  },
  {
    name: 'asname',
    label: 'AS Name',
    type: 'string' as const,
  },
  {
    name: 'asnumber',
    label: 'AS Number',
    type: 'string' as const,
  },
  {
    name: 'origins',
    label: 'Origin',
    type: 'string' as const,
  },
  {
    name: 'visited',
    label: 'Last Seen',
    type: 'special' as const,
  },
  {
    name: 'port',
    label: 'Port',
    type: 'string' as const,
  },
  {
    name: 'protocol',
    label: 'Protocol',
    type: 'string' as const,
  },
  {
    name: 'cloud',
    label: 'Cloud',
    type: 'string' as const,
  },
  {
    name: 'class',
    label: 'IP Version',
    type: 'select' as const,
    options: [
      { label: 'IPv4', value: 'ipv4' },
      { label: 'IPv6', value: 'ipv6' },
    ],
  },
  {
    name: 'tags',
    label: 'Tags',
    type: 'string' as const,
  },
  {
    name: 'attackSurface',
    label: 'Attack Surface',
    type: 'select' as const,
    options: [
      { label: 'Internal', value: 'internal' },
      { label: 'External', value: 'external' },
      { label: 'Cloud', value: 'cloud' },
      { label: 'Repository', value: 'repository' },
    ],
  },
  {
    name: 'created',
    label: 'Created',
    type: 'date' as const,
  },
];

/**
 * Seed field definitions
 * Used in QueryBuilder and other components for field configuration
 */
export const SEED_FIELDS = [
  {
    name: 'status',
    label: 'Status',
    type: 'select' as const,
    options: [
      { label: 'Active', value: 'A' },
      { label: 'Pending', value: 'P' },
      { label: 'Deleted', value: 'D' },
      { label: 'Rejected', value: 'R' },
      { label: 'Frozen', value: 'F' },
      { label: 'Frozen', value: 'FR' },
    ],
  },
  {
    name: 'origins',
    label: 'Origin',
    type: 'string' as const,
  },
  {
    name: 'registrant',
    label: 'Registrant',
    type: 'string' as const,
  },
  {
    name: 'customer',
    label: 'Customer',
    type: 'string' as const,
  },
  {
    name: 'country',
    label: 'Country',
    type: 'string' as const,
  },
  {
    name: 'email',
    label: 'Email',
    type: 'string' as const,
  },
  {
    name: 'registrar',
    label: 'Registrar',
    type: 'string' as const,
  },
  {
    name: 'profile',
    label: 'Scan Profile',
    type: 'string' as const,
  },
];

/**
 * Asset status mapping
 * Maps UI status labels to backend status codes
 */
export const ASSET_STATUS_MAP: Record<string, string> = {
  Approved: 'A',
  Active: 'A',
  Pending: 'P',
  Unavailable: 'U',
  Deleted: 'D',
};

/**
 * Asset status options for dropdowns
 */
export const ASSET_STATUS_OPTIONS = [
  { id: 'active', value: 'A', label: 'Approved' },
  { id: 'pending', value: 'P', label: 'Pending' },
  { id: 'unavailable', value: 'U', label: 'Unavailable' },
  { id: 'frozen', value: 'F', label: 'Frozen' },
  { id: 'deleted', value: 'D', label: 'Deleted' },
];

// ============================================
// FIELD NAME MAPPINGS
// ============================================

/**
 * Maps frontend field names to backend field names
 * Used in query building and API requests
 */
export const FIELD_NAME_MAP: Record<string, Record<string, string>> = {
  Vulnerability: {
    severity: 'priority',
    tags: 'tags',
  },
  Asset: {
    tags: 'tags',
  },
  Technology: {
    // Technology fields typically map 1:1
  },
  Seed: {
    // Seed fields typically map 1:1
  },
  Job: {
    // Job fields typically map 1:1
  },
};

// ============================================
// OPERATOR MAPPINGS
// ============================================

/**
 * Maps frontend operators to backend operators
 */
export const OPERATOR_MAP: Record<string, string> = {
  equals: '=',
  'not equals': '!=',
  contains: 'CONTAINS',
  'does not contain': 'NOT CONTAINS',
  'starts with': 'STARTS WITH',
  'ends with': 'ENDS WITH',
  'is any of': 'IN',
  'is none of': 'NOT IN',
  'greater than': '>',
  'less than': '<',
  'greater than or equals': '>=',
  'less than or equals': '<=',
  between: 'BETWEEN',
  before: '<',
  after: '>',
  'in the last': '>=',
  exists: 'IS NOT NULL',
  'not exists': 'IS NULL',
};

// ============================================
// ENTITY TYPE MAPPINGS
// ============================================

/**
 * Maps entity types to their Neo4j labels
 */
export const ENTITY_TYPE_TO_LABEL_MAP: Record<string, string[]> = {
  Asset: ['Asset'],
  ActiveDirectory: ['ADObject'], // Active Directory entities use ADObject label
  Vulnerability: ['Risk'], // Backend uses 'Risk' for vulnerabilities
  Seed: ['Seed'],
  Technology: ['Technology'],
  Job: ['Job'],
  Credential: ['Credential'],
};

// ============================================
// TECHNOLOGY MAPPINGS
// ============================================

/**
 * Technology field definitions
 * Used in QueryBuilder for field configuration
 */
export const TECHNOLOGY_FIELDS = [
  {
    name: 'vendor',
    label: 'Vendor',
    type: 'string' as const,
  },
  {
    name: 'product',
    label: 'Product',
    type: 'string' as const,
  },
  {
    name: 'version',
    label: 'Version',
    type: 'string' as const,
  },
];

/**
 * Technology vendor display name mappings
 * Used to format vendor names for display
 */
export const TECHNOLOGY_VENDOR_MAP: Record<string, string> = {
  apache: 'Apache',
  microsoft: 'Microsoft',
  google: 'Google',
  amazon: 'Amazon',
  oracle: 'Oracle',
  ibm: 'IBM',
  vmware: 'VMware',
  redhat: 'Red Hat',
  nginx: 'NGINX',
  nodejs: 'Node.js',
  mysql: 'MySQL',
  mariadb: 'MariaDB',
  redis: 'Redis',
  mongodb: 'MongoDB',
  elasticsearch: 'Elasticsearch',
  rabbitmq: 'RabbitMQ',
  kafka: 'Kafka',
  zookeeper: 'Zookeeper',
  openssh: 'OpenSSH',
  openbsd: 'OpenBSD',
  postgresql: 'PostgreSQL',
  openldap: 'OpenLDAP',
  openvpn: 'OpenVPN',
  // Add more as needed
};

/**
 * Technology category display mappings
 */
export const TECHNOLOGY_CATEGORY_MAP: Record<string, string> = {
  'web-server': 'Web Server',
  database: 'Database',
  'programming-language': 'Programming Language',
  'operating-system': 'Operating System',
  framework: 'Framework',
  library: 'Library',
  container: 'Container',
  'cloud-service': 'Cloud Service',
  // Add more as needed
};

// ============================================
// UTILITY FUNCTIONS
// ============================================

/**
 * Get the mapped field name for a given entity type and field
 */
export function getMappedFieldName(
  entityType: string,
  fieldName: string
): string {
  // Check if there's a mapping for this entity type and field
  const entityMappings = FIELD_NAME_MAP[entityType];
  if (entityMappings && entityMappings[fieldName]) {
    return entityMappings[fieldName];
  }

  // No mapping found, return the field name as-is
  return fieldName;
}

/**
 * Get the mapped operator for query building
 */
export function getMappedOperator(operator: string): string {
  return OPERATOR_MAP[operator] || operator;
}

/**
 * Map status value for vulnerabilities or assets
 */
export function mapStatusValue(
  value: string | string[],
  entityType: 'Vulnerability' | 'Asset' = 'Vulnerability'
): string | string[] {
  const statusMap =
    entityType === 'Vulnerability'
      ? VULNERABILITY_STATUS_MAP
      : ASSET_STATUS_MAP;

  if (Array.isArray(value)) {
    return value.map(v => statusMap[v] || v);
  }
  return statusMap[value] || value;
}

/**
 * Map severity to priority for vulnerabilities
 */
export function mapSeverityToPriority(
  value: string | string[]
): number | number[] {
  if (Array.isArray(value)) {
    return value.map(v => {
      // Check if the value is already a numeric string (e.g., "0", "10", "20")
      const numValue = Number(v);
      if (!isNaN(numValue) && [0, 10, 20, 30, 40, 50].includes(numValue)) {
        return numValue;
      }

      // Try severity code first (C, H, M, etc.)
      if (SEVERITY_CODE_TO_PRIORITY_MAP[v] !== undefined) {
        return SEVERITY_CODE_TO_PRIORITY_MAP[v];
      }
      // Fall back to full name (Critical, High, etc.)
      return SEVERITY_TO_PRIORITY_MAP[v] ?? 60;
    });
  }

  // Check if the value is already a numeric string (e.g., "0", "10", "20")
  const numValue = Number(value);
  if (!isNaN(numValue) && [0, 10, 20, 30, 40, 50].includes(numValue)) {
    return numValue;
  }

  // Try severity code first (C, H, M, etc.)
  if (SEVERITY_CODE_TO_PRIORITY_MAP[value] !== undefined) {
    return SEVERITY_CODE_TO_PRIORITY_MAP[value];
  }
  // Fall back to full name (Critical, High, etc.)
  return SEVERITY_TO_PRIORITY_MAP[value] ?? 60;
}

/**
 * Map priority to severity for display
 */
export function mapPriorityToSeverity(priority: number): string {
  return PRIORITY_TO_SEVERITY_MAP[priority] || 'Unknown';
}

/**
 * Get entity labels for Neo4j queries
 */
export function getEntityLabels(entityType: string): string[] {
  // Handle ActiveDirectory as its own entity type
  if (entityType === 'ActiveDirectory') {
    return ['ADObject']; // Use ADObject label for Active Directory entities
  }

  return ENTITY_TYPE_TO_LABEL_MAP[entityType] || [entityType];
}

/**
 * Get vulnerability severity from status code
 * NOTE: For display purposes, use getVulnerabilitySeverityLabel and getVulnerabilityStatusLabel
 * from vulnerability.util.ts instead, as they provide better fallbacks and formatting.
 */
export function getVulnerabilitySeverityFromStatus(statusCode: string): string {
  if (statusCode.length >= 2) {
    const definition = VULNERABILITY_STATUS_DEFINITIONS[statusCode];
    if (definition) {
      return definition.severity;
    }
  }
  return 'Unknown';
}

/**
 * Format technology vendor name for display
 */
export function formatTechnologyVendor(vendor: string): string {
  return TECHNOLOGY_VENDOR_MAP[vendor.toLowerCase()] || vendor;
}

/**
 * Format technology category for display
 */
export function formatTechnologyCategory(category: string): string {
  return TECHNOLOGY_CATEGORY_MAP[category.toLowerCase()] || category;
}

/**
 * Get status operator for special handling
 */
export function getStatusOperator(
  operator: string,
  isMultiValue: boolean
): string {
  // Special handling for status fields
  if (operator === '=' && isMultiValue) {
    return 'IN';
  }
  if (operator === '!=' && isMultiValue) {
    return 'NOT IN';
  }
  return operator;
}

/**
 * General pretty name mappings
 * Used for formatting various values for display
 */
export const PRETTY_NAME_MAP: Record<string, string> = {
  qualys: 'Qualys',
  rapid7: 'Rapid7',
  cidr: 'CIDR',
  domain: 'Domain',
  tld: 'TLD',
  ipv4: 'IPv4',
  ipv6: 'IPv6',
  s3: 'S3',
  gcp: 'Google Cloud Platform',
  repository: 'Repository',
  github: 'GitHub',
  tenablevm: 'Tenable VM',
};

/**
 * Format attack surface values with abstraction logic
 * Converts "Internal#wiz" -> "Internal", "External#github" -> "External", etc.
 */
export function formatAttackSurfaceLabel(value: string): string {
  if (!value) return '';

  // Attack surface abstraction: "Internal#wiz" → "Internal"
  if (value.includes('#')) {
    const prefix = value.split('#')[0].toLowerCase();

    const attackSurfaceMap: Record<string, string> = {
      internal: 'Internal',
      external: 'External',
      cloud: 'Cloud',
      repository: 'Repository',
    };

    return (
      attackSurfaceMap[prefix] ||
      prefix.charAt(0).toUpperCase() + prefix.slice(1)
    );
  }

  // For non-prefixed values, just capitalize
  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
}

/**
 * Format any value for pretty display
 * Checks technology vendors first, then general mappings
 */
export function formatPrettyName(value: string): string {
  if (!value) return '';

  // Check technology vendor mappings first
  const vendorMapped = TECHNOLOGY_VENDOR_MAP[value.toLowerCase()];
  if (vendorMapped) return vendorMapped;

  // Then check general pretty name mappings
  const prettyMapped = PRETTY_NAME_MAP[value.toLowerCase()];
  if (prettyMapped) return prettyMapped;

  // Default formatting: capitalize first letter of each word
  return value
    .split(/[-_\s]/)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

/**
 * Severity order for sorting vulnerabilities
 * Lower numbers = higher severity (Critical = 0, High = 10, etc.)
 */
export const VULNERABILITY_SEVERITY_ORDER = [0, 10, 20, 30, 40, 50];

/**
 * Sort vulnerabilities by severity (Critical to Info) and then by updated date
 * This ensures consistent ordering across the application
 */
export function sortVulnerabilitiesBySeverity<
  T extends { priority?: number; updated?: string | Date },
>(vulnerabilities: T[]): T[] {
  return [...vulnerabilities].sort((a, b) => {
    // First sort by severity (priority) - lower numbers = higher severity
    const priorityA = a.priority ?? 60; // Default to lowest priority if missing
    const priorityB = b.priority ?? 60;

    if (priorityA !== priorityB) {
      return priorityA - priorityB; // Ascending order: 0 (Critical) comes before 50 (Exposure)
    }

    // If severity is the same, sort by updated date (most recent first)
    if (a.updated && b.updated) {
      const dateA = new Date(a.updated);
      const dateB = new Date(b.updated);
      return dateB.getTime() - dateA.getTime();
    }

    return 0;
  });
}

// ============================================
// ENTITY LABEL MAPPINGS
// ============================================

/**
 * Maps frontend entity names to backend Neo4j labels
 * Used in QueryBuilder for graph queries
 */
export const ENTITY_LABEL_MAP: Record<string, string> = {
  Vulnerability: 'Risk',
  Asset: 'Asset',
  Technology: 'Technology',
  Seed: 'Seed',
  Job: 'Job',
};

// ============================================
// FIELD TYPE MAPPINGS
// ============================================

/**
 * Maps field names to their types for query building
 * 'direct' means the field is directly on the entity
 * 'related' means the field is accessed through relationships (HAS_ATTRIBUTE or HAS_PORT)
 * 'complex' means special handling is needed for this field
 */
export const FIELD_TYPE_MAP: Record<string, 'direct' | 'related' | 'complex'> =
  {
    // Special fields with custom handling
    adObjectType: 'complex', // Uses Neo4j labels instead of properties
    // Common fields across entities
    name: 'direct',
    key: 'direct',
    status: 'direct',
    created: 'direct',
    visited: 'direct',
    ttl: 'direct',
    username: 'direct',

    // Vulnerability/Risk fields
    priority: 'direct',
    cvss: 'direct',
    epss: 'direct',
    kev: 'direct',
    exploit: 'direct',
    updated: 'direct',
    tags: 'direct',
    id: 'direct',
    title: 'direct',
    writeupId: 'direct',

    // Asset fields
    identifier: 'direct',
    group: 'direct',
    dns: 'direct',
    domain: 'direct',
    asname: 'direct',
    asnumber: 'direct',
    asrange: 'direct',
    source: 'direct',
    origins: 'direct',
    class: 'direct',
    private: 'direct',
    secret: 'direct',
    cloudService: 'direct',
    cloudAccount: 'direct',
    cloudId: 'direct',
    cloudRoot: 'direct',
    country: 'direct',
    province: 'direct',
    city: 'direct',
    purchased: 'direct',
    expiration: 'direct',
    registrant: 'direct',
    registrar: 'direct',
    surface: 'direct',
    attackSurface: 'direct',
    port: 'related',
    protocol: 'related',
    new: 'related',
    cloud: 'related',

    // Technology fields (direct properties on Technology node)
    vendor: 'direct',
    product: 'direct',
    version: 'direct',
    update: 'direct',
    edition: 'direct',
    language: 'direct',
    sw_edition: 'direct',
    swEdition: 'direct', // Both variants for compatibility
    target_sw: 'direct',
    targetSw: 'direct', // Both variants for compatibility
    target_hw: 'direct',
    targetHw: 'direct', // Both variants for compatibility
    other: 'direct',
    cpe: 'direct',
    part: 'direct',

    // Seed fields (country is already defined above in Asset fields)
    email: 'direct',
    type: 'direct',
  };

/**
 * Get fields for a given entity type
 * Returns the appropriate field definitions based on entity type
 */
export function getFieldsForEntity(entityType: string) {
  switch (entityType) {
    case 'Asset':
      return ASSET_FIELDS;
    case 'Vulnerability':
      return VULNERABILITY_FIELDS;
    case 'Technology':
      return TECHNOLOGY_FIELDS;
    case 'Seed':
      return SEED_FIELDS;
    default:
      return [];
  }
}

/**
 * Get field type for a specific entity and field
 * Returns 'direct' or 'related' based on the field type mapping
 */
export function getFieldType(
  entityType: string,
  fieldName: string
): 'direct' | 'related' | 'complex' {
  // First check if the field exists in FIELD_TYPE_MAP
  const fieldType = FIELD_TYPE_MAP[fieldName];
  if (fieldType) {
    return fieldType;
  }

  // Fallback to entity-specific logic for backwards compatibility
  if (entityType === 'Vulnerability') {
    // For vulnerabilities, tags are now direct fields
    if (['tags'].includes(fieldName)) {
      return 'direct';
    }
    return 'direct';
  }

  if (entityType === 'Asset') {
    // For assets, these fields are stored as related attributes
    // Note: 'port' and 'protocol' are now handled via HAS_PORT relationships (see filterProcessor.ts)
    if (['cloud', 'new', 'source'].includes(fieldName)) {
      return 'related';
    }
    return 'direct';
  }

  if (entityType === 'Technology') {
    // All defined technology fields are direct properties on the backend model.
    return 'direct';
  }

  if (entityType === 'Seed') {
    // All seed fields are direct properties on the Seed model
    return 'direct';
  }

  if (entityType === 'ADObject' || entityType === 'ActiveDirectory') {
    // AD Object boolean fields are stored as direct properties on the Neo4j nodes
    const adBooleanFields = [
      'admincount',
      'isaclprotected',
      'pwdneverexpires',
      'isdc',
      'unconstraineddelegation',
      'ldapavailable',
      'ldapsavailable',
      'authenticationenabled',
      'adminsdholderprotected',
      'blocksinheritance',
      'dontreqpreauth',
      'passwordexpired',
      'smartcardrequired',
      'trustedtoauth',
      'enrolleesuppliessubject',
      'requiresmanagerapproval',
      'sensitive',
      'hasspn',
      'passwordnotreqd',
      'subjectaltrequireemail',
      'subjectaltrequireupn',
      'subjectrequireemail',
      'schannelauthenticationenabled',
    ];

    if (adBooleanFields.includes(fieldName)) {
      return 'direct';
    }

    // Other AD fields are typically direct properties too (including samaccountname)
    return 'direct';
  }

  return 'complex';
}
